import { z } from 'zod';
import { createPaginatedSchema, paginationQueryParamsSchema } from '../common/index.js';
/**
 * Event speaker — a RAW CRM employee passthrough embedded in the event payload.
 *
 * Unlike `/employee`, the event endpoints (`EventDetail` / `EventList`) return
 * speakers as a lighter CRM passthrough (raw field names, mostly optional).
 */
export const eventSpeakerSchema = z.object({
    name: z.string(),
    job_title: z.string().optional(),
    headshot_image_url: z.string().optional(),
    agent_website_id: z.string().optional(),
    linkedin_url: z.string().optional(),
});
export const eventUserRsvpStatusSchema = z.enum([
    'Registered',
    'Unpaid',
    'Attended',
    'WalkIn',
    'Cancelled',
    'NotAttended',
]);
export const eventUserRsvpAttendeeSchema = z.object({
    first_name: z.string(),
    last_name: z.string(),
    send_invite: z.boolean(),
    email: z.string().optional(),
});
export const eventUserRsvpSpouseSchema = eventUserRsvpAttendeeSchema.extend({
    contact_id: z.string().optional(),
});
export const eventUserRsvpSchema = z.object({
    status: eventUserRsvpStatusSchema,
    has_spouse: z.boolean(),
    guest_count: z.number().int().min(0).max(4),
    spouse: eventUserRsvpSpouseSchema.nullable().optional(),
    guests: z.array(eventUserRsvpAttendeeSchema),
    goto_join_url: z.url().optional(),
});
export const eventStatusEnum = z.enum([
    'Planned',
    'Pending',
    'Booked',
    'Published',
    'Closed',
    'Complete',
    'Cancelled',
    'Denied',
]);
export const eventSchema = z.object({
    id: z.string(),
    name: z.string(),
    date_start: z.iso.datetime(),
    date_end: z.iso.datetime(),
    time_zone: z.string(),
    address: z.string(),
    location_name: z.string().optional(),
    city: z.string().optional(),
    featured_photo: z.url(),
    type: z.string(),
    status: eventStatusEnum,
    is_online: z.boolean(),
    is_private: z.boolean(),
    description: z.string(),
    parking_instructions: z.string().optional(),
    admission_fee: z.number(),
    crm_link: z.url().optional(),
    speakers: z.array(eventSpeakerSchema),
    rsvp_count: z.number(),
    max_capacity: z.number().int().nonnegative().optional(),
    is_full: z.boolean().optional(),
    spots_remaining: z.number().int().nonnegative().optional(),
    markets: z.array(z.string()).optional(),
    user_rsvp: eventUserRsvpSchema.optional(),
});
export const eventQueryParamsSchema = paginationQueryParamsSchema.extend({
    event_is_online: z.literal(['true', 'false']).optional(),
    event_start_gte: z.iso.datetime().optional(),
    event_start_lte: z.iso.datetime().optional(),
});
export const paginatedEventSchema = createPaginatedSchema(eventSchema);
